import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SiteHeader, SiteFooter } from '../components/SiteChrome';
import {
  Users, Wrench, GraduationCap, Boxes, ArrowRight, ArrowDown, Plane, ClipboardList,
  ClipboardCheck, FlaskConical, Cpu, Handshake, CalendarClock, Building2, Presentation,
  Layers, Home, MapPin, Phone, Mail, ChevronRight
} from 'lucide-react';

const IMG = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/4cd02427-b7f1-4429-bc2d-baceb69a3fd9/images/a9430b343c22ead381c774a5447354aa67437f0b5337e241d7e22eeeb076c5bd.jpeg',
  facility: 'https://static.prod-images.emergentagent.com/jobs/4cd02427-b7f1-4429-bc2d-baceb69a3fd9/images/687c117c0da081adf229f6e4ec9286192e71f44a547402ce12e34b4a676c5850.jpeg',
  g1: 'https://images.unsplash.com/photo-1543071293-d91175a68672?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  g2: 'https://images.unsplash.com/photo-1431540015161-0bf868a2d407?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  g3: 'https://images.unsplash.com/photo-1603201667141-5a2d4c673378?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  g4: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  g5: 'https://images.unsplash.com/photo-1778877035089-c80dcb877f45?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  g6: 'https://images.unsplash.com/photo-1766247096032-66a4d647d1ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  res1: 'https://static.prod-images.emergentagent.com/jobs/4cd02427-b7f1-4429-bc2d-baceb69a3fd9/images/64b141ac7f5c5e753cbc892b998ddc6bb848c1972c43922eecc6821a3bcbbf3a.jpeg',
  res2: 'https://static.prod-images.emergentagent.com/jobs/4cd02427-b7f1-4429-bc2d-baceb69a3fd9/images/c54afd9a8a5e55f7642bafc4743a5648ef60717f29b7da66b6c82cb7aebde313.jpeg',
  res3: 'https://static.prod-images.emergentagent.com/jobs/4cd02427-b7f1-4429-bc2d-baceb69a3fd9/images/14246badb3ea78fec987e46c638096abdceba44d4df1228ab952a611c259f55b.jpeg',
  synergomodule: 'https://customer-assets-39nsmqrw.emergentagent.net/job_synergo-atom/artifacts/3031728a_Picsart_26-07-27_11-41-06-832.webp',
  centrumMifre: 'https://customer-assets-39nsmqrw.emergentagent.net/job_synergo-atom/artifacts/tj7lhqsl_Picsart_26-07-27_11-42-06-962.png',
};

const T = {
  cs: {
    hero: { kicker: 'SYNERGOATOM SE', title: 'SYNERGOMODULE', platform: 'Platforma pro inženýrskou spolupráci', desc: 'Podpora mezinárodních inženýrských týmů prostřednictvím flexibilní modulární infrastruktury.', cooperation: 'Ve spolupráci s', learn: 'Zjistit více', contact: 'Kontaktujte nás' },
    about: {
      title: 'O iniciativě',
      subtitle: 'Strategická infrastruktura pro mezinárodní inženýrskou spolupráci',
      p1: 'SYNERGOATOM SE rozvíjí modulární infrastrukturu, která je určena k podpoře mezinárodních inženýrských projektů a dlouhodobé technické spolupráce.',
      p2: 'Infrastruktura se nachází v přírodním prostředí v blízkosti Prahy a může být využívána jako praktická inženýrská platforma pro mobilitu odborníků, přípravu projektů a rozvoj technologií.',
      usersTitle: 'Zázemí je určeno pro:',
      users: ['Hostující odborníky', 'Projektové týmy', 'Výzkumné pracovníky', 'Průmyslové partnery', 'Univerzity', 'Technické semináře', 'Workshopy', 'Inženýrskou koordinaci'],
    },
    why: {
      title: 'Proč modulární infrastruktura',
      cards: [
        { icon: 'Users', title: 'Mobilita mezinárodních odborníků', desc: 'Flexibilní ubytování pro technické specialisty pracující na mezinárodních projektech.' },
        { icon: 'Wrench', title: 'Inženýrská spolupráce', desc: 'Pracovní prostředí podporující multidisciplinární inženýrské týmy.' },
        { icon: 'GraduationCap', title: 'Výměna znalostí', desc: 'Prostory vhodné pro workshopy, semináře a technická jednání.' },
        { icon: 'Boxes', title: 'Škálovatelná infrastruktura', desc: 'Modulární koncept umožňující budoucí rozšíření podle potřeb projektu.' },
      ],
    },
    stats: { title: 'V přehledu', items: [
      { value: 4, suffix: '', label: 'Klíčové oblasti' },
      { value: 8, suffix: '', label: 'Typické aktivity' },
      { value: 7, suffix: '', label: 'Typy partnerů' },
      { value: 7, suffix: '', label: 'Způsoby využití' },
    ] },
    eco: { title: 'Inženýrský ekosystém', desc: 'CENTRUM MIFRE propojuje odborníky, instituce a projekty do jednoho spolupracujícího prostředí.', center: 'SYNERGOMODULE', nodes: ['SYNERGOATOM SE', 'Mezinárodní odborníci', 'Univerzity', 'Výzkumné instituty', 'Průmysloví partneři', 'Vývojáři technologií', 'Inženýrské projekty'] },
    activities: { title: 'Typické aktivity', items: [
      { icon: 'Plane', label: 'Příjezd odborníků' },
      { icon: 'Wrench', label: 'Technické workshopy' },
      { icon: 'ClipboardList', label: 'Příprava projektů' },
      { icon: 'ClipboardCheck', label: 'Inženýrské přezkumy' },
      { icon: 'FlaskConical', label: 'Společný výzkum' },
      { icon: 'Cpu', label: 'Ukázky technologií' },
      { icon: 'Handshake', label: 'Mezinárodní spolupráce' },
      { icon: 'CalendarClock', label: 'Dlouhodobá podpora projektů' },
    ] },
    facilities: { title: 'Modulární zázemí', desc: 'Modulární budova představuje flexibilní infrastrukturu, kterou lze přizpůsobit potřebám projektu.', usesTitle: 'Možná využití:', uses: ['Ubytování', 'Projektová kancelář', 'Zasedací místnosti', 'Školicí zázemí', 'Technická koordinace', 'Inovační workshopy', 'Dočasné inženýrské ústředí'] },
    residential: {
      title: 'Moduly pro dlouhodobé ubytování',
      subtitle: 'Ubytování zahraničních specialistů',
      desc: 'Modulární obytné jednotky určené pro dlouhodobé ubytování zahraničních specialistů. V budoucnu mohou sloužit také mladým specialistům a jejich rodinám žijícím v bezprostřední blízkosti objektů jaderné infrastruktury České republiky. Komfortní, energeticky efektivní a rychle instalovatelné, s možností uspořádání do rezidenčního kampusu.',
      items: [
        { img: 'res1', cap: 'Samostatný obytný modul' },
        { img: 'res2', cap: 'Obytný modul v přírodním prostředí' },
        { img: 'res3', cap: 'Rezidenční kampus z modulů' },
      ],
    },
    gallery: { title: 'Galerie', items: [
      { img: 'g1', cap: 'Moderní modulární architektura' },
      { img: 'g2', cap: 'Zasedací místnost' },
      { img: 'g3', cap: 'Společná práce inženýrů' },
      { img: 'g4', cap: 'Mezinárodní jednání' },
      { img: 'g5', cap: 'Školení' },
      { img: 'g6', cap: 'Krajina v okolí Prahy' },
    ] },
    future: { title: 'Budoucí rozvoj', p1: 'Modulární platforma je navržena tak, aby podporovala budoucí inženýrskou spolupráci.', p2: 'Podle budoucích mezinárodních projektů a provozních potřeb může být dále rozšiřována a přizpůsobována.', cta: 'Kontaktujte nás' },
  },
  en: {
    hero: { kicker: 'SYNERGOATOM SE', title: 'SYNERGOMODULE', platform: 'Engineering Collaboration Platform', desc: 'Supporting international engineering teams through flexible modular infrastructure.', cooperation: 'In cooperation with', learn: 'Learn More', contact: 'Contact Us' },
    about: {
      title: 'About the Initiative',
      subtitle: 'Strategic Infrastructure for International Engineering Cooperation',
      p1: 'SYNERGOATOM SE is developing modular infrastructure that is intended to support international engineering projects and long-term technical cooperation.',
      p2: 'Located in a natural environment near Prague, the infrastructure can be used as a practical engineering platform for expert mobility, project preparation and technology development.',
      usersTitle: 'The facilities are intended for:',
      users: ['Visiting experts', 'Project teams', 'Researchers', 'Industrial partners', 'Universities', 'Technical seminars', 'Workshops', 'Engineering coordination'],
    },
    why: {
      title: 'Why Modular Infrastructure',
      cards: [
        { icon: 'Users', title: 'International Expert Mobility', desc: 'Flexible accommodation for technical specialists working on international projects.' },
        { icon: 'Wrench', title: 'Engineering Collaboration', desc: 'Working environment supporting multidisciplinary engineering teams.' },
        { icon: 'GraduationCap', title: 'Knowledge Exchange', desc: 'Facilities suitable for workshops, seminars and technical meetings.' },
        { icon: 'Boxes', title: 'Scalable Infrastructure', desc: 'Modular concept allowing future expansion according to project needs.' },
      ],
    },
    stats: { title: 'At a glance', items: [
      { value: 4, suffix: '', label: 'Core capabilities' },
      { value: 8, suffix: '', label: 'Typical activities' },
      { value: 7, suffix: '', label: 'Partner types' },
      { value: 7, suffix: '', label: 'Modular uses' },
    ] },
    eco: { title: 'Engineering Ecosystem', desc: 'CENTRUM MIFRE connects experts, institutions and projects into a single collaborative environment.', center: 'SYNERGOMODULE', nodes: ['SYNERGOATOM SE', 'International Experts', 'Universities', 'Research Institutes', 'Industrial Partners', 'Technology Developers', 'Engineering Projects'] },
    activities: { title: 'Typical Activities', items: [
      { icon: 'Plane', label: 'Arrival of experts' },
      { icon: 'Wrench', label: 'Technical workshops' },
      { icon: 'ClipboardList', label: 'Project preparation' },
      { icon: 'ClipboardCheck', label: 'Engineering reviews' },
      { icon: 'FlaskConical', label: 'Joint research' },
      { icon: 'Cpu', label: 'Technology demonstrations' },
      { icon: 'Handshake', label: 'International cooperation' },
      { icon: 'CalendarClock', label: 'Long-term project support' },
    ] },
    facilities: { title: 'Modular Facilities', desc: 'The modular building represents flexible infrastructure that can be adapted to project needs.', usesTitle: 'Possible uses:', uses: ['Accommodation', 'Project office', 'Meeting rooms', 'Training facilities', 'Technical coordination', 'Innovation workshops', 'Temporary engineering headquarters'] },
    residential: {
      title: 'Modules for Long-Term Accommodation',
      subtitle: 'Accommodation of international specialists',
      desc: 'Modular residential units intended for the long-term accommodation of international specialists. They can subsequently also serve young specialists and their families living in the immediate vicinity of the nuclear infrastructure facilities of the Czech Republic. Comfortable, energy-efficient and quick to install, and able to be arranged into a residential campus.',
      items: [
        { img: 'res1', cap: 'Standalone residential module' },
        { img: 'res2', cap: 'Residential module in natural surroundings' },
        { img: 'res3', cap: 'Residential campus of modules' },
      ],
    },
    gallery: { title: 'Gallery', items: [
      { img: 'g1', cap: 'Modern modular architecture' },
      { img: 'g2', cap: 'Conference room' },
      { img: 'g3', cap: 'Engineers working together' },
      { img: 'g4', cap: 'International meetings' },
      { img: 'g5', cap: 'Training session' },
      { img: 'g6', cap: 'Landscape around Prague' },
    ] },
    future: { title: 'Future Development', p1: 'The modular platform is designed to support future engineering cooperation.', p2: 'It may be further expanded and adapted according to future international projects and operational needs.', cta: 'Contact Us' },
  },
};

const ICONS = { Users, Wrench, GraduationCap, Boxes, Plane, ClipboardList, ClipboardCheck, FlaskConical, Cpu, Handshake, CalendarClock, Presentation, Layers, Building2 };
const Icon = ({ name, className }) => { const C = ICONS[name]; return C ? <C className={className} /> : null; };

// Circle node positions (percentages) for the ecosystem network
const NODE_POS = [
  { x: 50, y: 8 }, { x: 82.8, y: 23.8 }, { x: 90.9, y: 59.3 }, { x: 68.2, y: 87.8 },
  { x: 31.8, y: 87.8 }, { x: 9.1, y: 59.3 }, { x: 17.2, y: 23.8 },
];

const Counter = ({ value, suffix = '' }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1200; const start = performance.now();
          const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            setN(Math.round(p * value));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);
  return <span ref={ref}>{n}{suffix}</span>;
};

export default function CentrumMifre({ lang, setLang }) {
  const navigate = useNavigate();
  const t = T[lang];

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('in-view'); observer.unobserve(entry.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [lang]);

  const scrollToAbout = () => document.getElementById('mifre-about')?.scrollIntoView({ behavior: 'smooth' });
  const goContact = () => navigate('/', { state: { scrollTo: 'contact' } });

  return (
    <div className="min-h-screen bg-slate-900 text-white font-['Space_Grotesk',sans-serif]" data-testid="mifre-page">
      <SiteHeader lang={lang} setLang={setLang} />

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center pt-20 md:bg-fixed" style={{ backgroundImage: `linear-gradient(to bottom, rgba(15,23,42,0.65), rgba(15,23,42,0.92)), url(${IMG.hero})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center hero-content">
          <img src={IMG.synergomodule} alt="SYNERGOMODULE" className="h-20 sm:h-28 w-auto mx-auto mb-5 object-contain drop-shadow-lg" />
          <p className="text-[#D4A84B] uppercase tracking-[0.25em] text-xs sm:text-sm mb-4">{t.hero.kicker}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Syne',sans-serif] tracking-tight">{t.hero.title}</h1>
          <p className="text-lg sm:text-2xl text-[#D4A84B] font-medium mb-6 font-['Syne',sans-serif]">{t.hero.platform}</p>
          <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto mb-8">{t.hero.desc}</p>
          <div className="flex flex-col items-center gap-2 mb-10">
            <span className="text-gray-400 text-[11px] uppercase tracking-widest">{t.hero.cooperation}</span>
            <a href="https://www.centrummifre.cz/" target="_blank" rel="noopener noreferrer" data-testid="mifre-external-link" className="inline-block opacity-90 hover:opacity-100 transition-opacity" title="CENTRUM MIFRE">
              <img src={IMG.centrumMifre} alt="CENTRUM MIFRE" className="h-7 sm:h-9 w-auto object-contain" />
            </a>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={scrollToAbout} data-testid="mifre-learn-more" className="px-8 py-3 bg-[#D4A84B] text-slate-900 font-semibold rounded-lg hover:bg-[#c49a3f] transition-colors inline-flex items-center justify-center gap-2">{t.hero.learn}<ArrowDown className="w-4 h-4" /></button>
            <button onClick={goContact} data-testid="mifre-contact-us" className="px-8 py-3 border border-[#D4A84B] text-[#D4A84B] font-semibold rounded-lg hover:bg-[#D4A84B]/10 transition-colors inline-flex items-center justify-center gap-2">{t.hero.contact}<ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="mifre-about" className="py-16 sm:py-24 bg-slate-900 reveal">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#D4A84B] uppercase tracking-widest text-xs sm:text-sm mb-3 text-center">{t.about.subtitle}</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 font-['Syne',sans-serif] text-center">{t.about.title}</h2>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">{t.about.p1}</p>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">{t.about.p2}</p>
              <p className="text-sm font-semibold text-[#D4A84B] uppercase tracking-wide mb-4">{t.about.usersTitle}</p>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {t.about.users.map((u, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-300"><ChevronRight className="w-4 h-4 text-[#D4A84B] flex-shrink-0" /><span>{u}</span></div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src={IMG.facility} alt="Modular engineering facility" className="rounded-2xl border border-slate-700 w-full h-auto shadow-2xl" loading="lazy" />
              <div className="absolute -bottom-4 -right-4 hidden sm:block bg-slate-800 border border-slate-700 rounded-xl px-5 py-3">
                <p className="text-[#D4A84B] font-semibold text-sm">Praha · Czech Republic</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why modular */}
      <section className="py-16 sm:py-24 bg-slate-800/30 reveal">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 font-['Syne',sans-serif] text-center">{t.why.title}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.why.cards.map((c, i) => (
              <div key={i} data-testid={`mifre-feature-${i}`} className="group bg-slate-800/50 border border-slate-700 rounded-2xl p-7 hover:border-[#D4A84B]/60 hover:-translate-y-1.5 transition-all duration-300">
                <div className="w-14 h-14 rounded-xl bg-[#D4A84B]/10 flex items-center justify-center mb-5 group-hover:bg-[#D4A84B]/20 transition-colors">
                  <Icon name={c.icon} className="w-7 h-7 text-[#D4A84B]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-3 font-['Syne',sans-serif]">{c.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats counters */}
      <section className="py-14 bg-slate-900 reveal">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {t.stats.items.map((s, i) => (
              <div key={i} className="text-center border border-slate-800 rounded-2xl py-8 bg-slate-800/30">
                <div className="text-4xl sm:text-5xl font-bold text-[#D4A84B] font-['Syne',sans-serif]"><Counter value={s.value} suffix={s.suffix} /></div>
                <p className="text-gray-400 text-sm mt-2">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engineering ecosystem */}
      <section className="py-16 sm:py-24 bg-slate-800/30 reveal">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Syne',sans-serif] text-center">{t.eco.title}</h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-14">{t.eco.desc}</p>

          {/* Desktop radial network */}
          <div className="relative mx-auto hidden md:block" style={{ maxWidth: '760px', aspectRatio: '1 / 0.78' }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              {NODE_POS.map((p, i) => (
                <line key={i} x1="50" y1="50" x2={p.x} y2={p.y} stroke="#D4A84B" strokeWidth="0.25" strokeOpacity="0.5" className="eco-line" />
              ))}
            </svg>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-[#D4A84B] text-slate-900 flex items-center justify-center text-center font-bold font-['Syne',sans-serif] shadow-[0_0_40px_rgba(212,168,75,0.35)] z-10 px-4">
              {t.eco.center}
            </div>
            {NODE_POS.map((p, i) => (
              <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 w-32 z-10" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
                <div className="bg-slate-900 border border-slate-700 hover:border-[#D4A84B] rounded-xl px-3 py-2.5 text-center text-xs text-gray-200 transition-colors shadow-lg">{t.eco.nodes[i]}</div>
              </div>
            ))}
          </div>

          {/* Mobile stacked network */}
          <div className="md:hidden flex flex-col items-center gap-3">
            <div className="w-full max-w-xs bg-[#D4A84B] text-slate-900 rounded-xl px-4 py-4 text-center font-bold font-['Syne',sans-serif]">{t.eco.center}</div>
            <ArrowDown className="w-5 h-5 text-[#D4A84B]" />
            <div className="grid grid-cols-1 gap-2.5 w-full max-w-xs">
              {t.eco.nodes.map((nd, i) => (
                <div key={i} className="bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-center text-sm text-gray-200">{nd}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Typical activities timeline */}
      <section className="py-16 sm:py-24 bg-slate-900 reveal">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-14 font-['Syne',sans-serif] text-center">{t.activities.title}</h2>
          <div className="relative pl-4">
            <div className="absolute left-[27px] top-2 bottom-2 w-px bg-slate-700" />
            <div className="space-y-6">
              {t.activities.items.map((a, i) => (
                <div key={i} className="relative flex items-start gap-5">
                  <div className="relative z-10 w-12 h-12 flex-shrink-0 rounded-full bg-slate-800 border border-[#D4A84B]/40 flex items-center justify-center">
                    <Icon name={a.icon} className="w-5 h-5 text-[#D4A84B]" />
                  </div>
                  <div className="bg-slate-800/40 border border-slate-700 rounded-xl px-5 py-4 flex-1">
                    <span className="text-[#D4A84B]/70 text-xs font-mono">{String(i + 1).padStart(2, '0')}</span>
                    <p className="text-white font-medium">{a.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Modular facilities */}
      <section className="py-16 sm:py-24 bg-slate-800/30 reveal">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="order-2 lg:order-1">
              <img src={IMG.facility} alt="Modular facility interior" className="rounded-2xl border border-slate-700 w-full h-auto shadow-2xl" loading="lazy" />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5 font-['Syne',sans-serif]">{t.facilities.title}</h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">{t.facilities.desc}</p>
              <p className="text-sm font-semibold text-[#D4A84B] uppercase tracking-wide mb-4">{t.facilities.usesTitle}</p>
              <div className="flex flex-wrap gap-3">
                {t.facilities.uses.map((u, i) => (
                  <span key={i} className="bg-slate-900 border border-slate-700 rounded-full px-4 py-2 text-sm text-gray-300">{u}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Residential modules */}
      <section className="py-16 sm:py-24 bg-slate-900 reveal">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#D4A84B] uppercase tracking-widest text-xs sm:text-sm mb-3 text-center">{t.residential.subtitle}</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5 font-['Syne',sans-serif] text-center">{t.residential.title}</h2>
          <p className="text-gray-400 text-center max-w-3xl mx-auto mb-12">{t.residential.desc}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.residential.items.map((r, i) => (
              <div key={i} data-testid={`mifre-residential-${i}`} className="group overflow-hidden rounded-2xl border border-slate-700 bg-slate-800/40 hover:border-[#D4A84B]/60 hover:-translate-y-1 transition-all duration-300">
                <div className="overflow-hidden aspect-[3/2]">
                  <img src={IMG[r.img]} alt={r.cap} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <p className="px-5 py-4 text-sm text-gray-200">{r.cap}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 sm:py-24 bg-slate-800/30 reveal">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 font-['Syne',sans-serif] text-center">{t.gallery.title}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.gallery.items.map((g, i) => (
              <div key={i} className="group relative overflow-hidden rounded-2xl border border-slate-700 aspect-[4/3]">
                <img src={IMG[g.img]} alt={g.cap} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/10 to-transparent" />
                <p className="absolute bottom-4 left-4 right-4 text-white text-sm font-medium">{g.cap}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future development */}
      <section className="py-16 sm:py-24 bg-slate-900 reveal">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#D4A84B]/10 flex items-center justify-center">
            <Layers className="w-8 h-8 text-[#D4A84B]" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Syne',sans-serif]">{t.future.title}</h2>
          <p className="text-gray-300 text-lg leading-relaxed mb-4">{t.future.p1}</p>
          <p className="text-gray-400 text-lg leading-relaxed mb-10">{t.future.p2}</p>
          <button onClick={goContact} data-testid="mifre-future-contact" className="inline-flex items-center gap-3 px-8 py-4 bg-[#D4A84B] text-slate-900 font-semibold rounded-lg hover:bg-[#c49a3f] transition-colors">{t.future.cta}<ArrowRight className="w-5 h-5" /></button>
        </div>
      </section>

      <SiteFooter lang={lang} />
    </div>
  );
}
